import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import videoSrc from '../Assets/Videos/Drone_Video.mp4';
import KiaanButton from './KiaanButton';

const acreageData = [
  { year: '2023-24', acres: 25, label: 'Yelakki banana', projects: null },
  { year: '2024-25', acres: 65, label: '3 farm projects', projects: 3 },
  { year: '2025-26', acres: 100, label: '3 farm projects', projects: 3 },
];

const highlights = [
  {
    title: 'Asset-light model',
    desc: 'Farmland leased, not purchased',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75S3.5 8 17 8z" />
      </svg>
    ),
  },
  {
    title: '2 to 3 crop cycles',
    desc: 'Committed to every leased parcel',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
        <path d="M21 3v5h-5" />
      </svg>
    ),
  },
  {
    title: 'Expert-led operations',
    desc: 'From land selection through to harvest',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
      </svg>
    ),
  },
];

const Story = () => {
  const [typedStory, setTypedStory] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const fullStory = "Three production cycles. Real challenges. Real learnings.";
  const sectionRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    let storyIndex = 0;
    let typingTimeout;

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2,
    };

    const handleIntersection = (entries) => {
      const [entry] = entries;
      setIsTyping(entry.isIntersecting);
      if (entry.isIntersecting && videoRef.current) {
        videoRef.current.play();
      } else if (videoRef.current) {
        videoRef.current.pause();
      }
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const typeText = () => {
      if (storyIndex < fullStory.length) {
        setTypedStory(fullStory.slice(0, storyIndex + 1));
        storyIndex++;
        typingTimeout = setTimeout(typeText, 30);
      }
    };

    if (isTyping) {
      typeText();
    }

    return () => {
      clearTimeout(typingTimeout);
      observer.disconnect();
    };
  }, [isTyping]);

  const maxAcres = 100;

  return (
    <section
      ref={sectionRef}
      className="relative py-12 md:py-20 bg-gradient-to-br from-green-100 to-green-300 overflow-hidden"
    >
      {/* Decorative background accents (same green family) */}
      <div className="pointer-events-none absolute -top-16 -left-16 w-72 h-72 bg-green-200/50 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute top-40 -right-20 w-80 h-80 bg-green-400/20 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10" id="about">
        {/* Top: Video + Narrative */}
        <div className="flex flex-col lg:flex-row lg:items-stretch gap-8 lg:gap-10 mb-16">
          {/* Left side — video + highlights (fills the column) */}
          <div className="lg:w-1/2 animateFadeInLeft flex flex-col gap-5">
            <div className="relative">
              {/* Offset backdrop for layered depth */}
              <div className="absolute -bottom-4 -right-4 w-full h-full rounded-2xl bg-green-400/30 hidden sm:block" />
              <div className="relative overflow-hidden rounded-2xl ring-1 ring-green-900/10 shadow-[0_25px_60px_-20px_rgba(2,58,16,0.55)]">
                <video
                  ref={videoRef}
                  className="w-full block transition-transform duration-700 ease-in-out transform hover:scale-105"
                  loop
                  muted
                  playsInline
                  src={videoSrc}
                  onMouseEnter={() => videoRef.current.play()}
                  onMouseLeave={() => videoRef.current.pause()}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            {/* Story highlights — grows to keep both columns balanced */}
            <div className="flex-1 bg-white/40 backdrop-blur-sm rounded-2xl ring-1 ring-white/60 shadow-sm p-5 md:p-6 flex flex-col justify-center gap-4">
              {highlights.map((h, i) => (
                <div key={i} className="flex items-center gap-4">
                  <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-[rgb(2,58,16)] text-white flex items-center justify-center">
                    {h.icon}
                  </span>
                  <div>
                    <div className="text-base font-bold text-[rgb(2,58,16)]">{h.title}</div>
                    <div className="text-sm text-gray-700 font-serif">{h.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right side story text */}
          <div className="lg:w-1/2 animateFadeInRight flex flex-col">
            <div className="text-left">
              <span className="inline-block bg-[rgb(2,58,16)]/10 text-[rgb(2,58,16)] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3">
                Our Journey
              </span>
              <h2 className="text-4xl md:text-5xl text-[rgb(2,58,16)] mb-3 font-bold leading-tight">Our Story So Far</h2>
              <h3 className="text-2xl mb-5 text-green-700 font-medium min-h-[2rem]">
                {typedStory}
                <span className="inline-block w-0.5 h-5 md:h-6 bg-green-700 ml-1 align-middle animate-pulse" />
              </h3>
            </div>
            <p className="font-serif text-lg mb-5 text-gray-800 border-l-4 border-green-600/60 pl-4">
              Agriculture has traditionally excluded those without land or time to farm. Kiaan Agrowtech's farming partnership model opens this up.
            </p>
            <p className="font-serif text-base mb-4 text-gray-700 leading-relaxed">
              Kiaan Agrowtech began with a simple conviction: banana cultivation, run with scientific rigour
              and operational discipline, can be both commercially scalable and reliably profitable. We adopted
              an asset-light farming model — leasing agricultural land rather than acquiring it — and committed
              to cultivating each leased parcel across 2 to 3 full crop cycles, engaging agricultural experts
              from land selection through to harvest.
            </p>
            <p className="font-serif text-base mb-6 text-gray-700 leading-relaxed">
              What follows is not a polished pitch. It is the honest account of three real production cycles —
              what we got right, what challenged us, and how each season made our operations stronger. We believe
              this transparency is what builds genuine confidence for anyone joining us as a farming partner.
            </p>
            <div className="mt-auto">
              <KiaanButton href="#partnership">
                <span className="relative z-10 font-semibold text-green-950">Join as a Partner</span>
              </KiaanButton>
            </div>
          </div>
        </div>

        {/* Bottom: Stats Card + Acreage Growth Chart */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg p-6 md:p-10 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {/* Header */}
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-[rgb(2,58,16)]">Our story so far</h3>
            <p className="text-gray-500 text-sm font-serif">Kiaan Agrowtech — growing acreage, hands-on experience</p>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-xl p-5">
              <div className="text-4xl font-bold text-[rgb(2,58,16)]">3</div>
              <div className="text-gray-600 text-sm font-serif mt-1">Production cycles completed</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-5">
              <div className="text-4xl font-bold text-[rgb(2,58,16)]">100 acres</div>
              <div className="text-gray-600 text-sm font-serif mt-1">Under cultivation today</div>
            </div>
          </div>

          {/* Acreage Growth Chart */}
          <div>
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-6">Acreage Growth by Year</h4>
            <div className="space-y-6">
              {acreageData.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  viewport={{ once: true }}
                >
                  {/* Acre label above bar */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-500 font-serif">{item.year}</span>
                    <span className="text-sm font-bold text-[rgb(2,58,16)]">{item.acres} acres</span>
                  </div>
                  {/* Bar */}
                  <div className="w-full bg-gray-100 rounded-lg h-12 relative overflow-hidden">
                    <motion.div
                      className="h-full rounded-lg flex items-center px-4"
                      style={{
                        backgroundColor: index === 0 ? '#a7d8c4' : index === 1 ? '#6bbfa8' : '#4aab91',
                      }}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(item.acres / maxAcres) * 100}%` }}
                      transition={{ duration: 0.8, delay: index * 0.2 }}
                      viewport={{ once: true }}
                    >
                      <span className="text-sm font-medium text-[rgb(2,58,16)] whitespace-nowrap">
                        {item.label}
                      </span>
                    </motion.div>
                  </div>
                  {/* Sub-label */}
                  {item.projects && (
                    <div className="text-xs text-gray-400 mt-1 font-serif">{item.projects} farm projects</div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Story;
